import type { ArenaPointDef, ArenaRectDef, ArenaTemplateDef } from './types';

/*
 * A port of shared-cs ArenaGenerator's nav grid (`Generate`'s size and cell rules, `Grid`, `Inside`) and ArenaRules' shortest legal run
 * (`ShortestRun`, `NearestFree`, `Overlaps`, the 8-connected Dijkstra), so the template preview draws the same line the validator and
 * `ArenaRules.MinRunSeconds` measure. Like the validator, the preview fills every wreck slot (the worst seed). Keep in sync with
 * packages/shared-cs/Runtime/Arenas/ArenaGenerator.cs and ArenaRules.cs.
 */

/** shared-cs `ArenaGenerator.WreckRadiusM`. */
export const WRECK_RADIUS_M = 2.2;

export type ArenaGrid = {
  cols: number;
  rows: number;
  cellM: number;
  sizeX: number;
  sizeY: number;
  /** Row-major solid cells: blocks and wrecks. */
  blocked: Uint8Array;
};

const num = (v: number | null | undefined, d = 0) =>
  typeof v === 'number' && Number.isFinite(v) ? v : d;

export function templateSize(t: ArenaTemplateDef): [number, number] {
  const s = t.sizeM ?? [];
  return [Math.max(0, num(s[0])), Math.max(0, num(s[1]))];
}

/** `ArenaGenerator.Inside`: half-open on the far edges. */
export const inside = (r: ArenaRectDef, x: number, y: number) =>
  x >= num(r.x) && x < num(r.x) + num(r.w) && y >= num(r.y) && y < num(r.y) + num(r.h);

/** `ArenaGenerator.Generate`'s grid with every wreck slot filled; null above 400 x 400 cells (a broken template the validator reports). */
export function buildGrid(
  t: ArenaTemplateDef,
  wrecks: ArenaPointDef[] | 'all' = 'all',
): ArenaGrid | null {
  const s = t.sizeM ?? [];
  const sizeX = s.length > 0 ? Math.max(1, num(s[0])) : 220;
  const sizeY = s.length > 1 ? Math.max(1, num(s[1])) : 160;
  const cellM = num(t.cellM) > 0.25 ? num(t.cellM) : 2;
  const cols = Math.max(1, Math.ceil(sizeX / cellM));
  const rows = Math.max(1, Math.ceil(sizeY / cellM));
  if (cols > 400 || rows > 400) return null;
  const blocks = (t.blocks ?? []).filter((b): b is ArenaRectDef => !!b);
  const discs = (wrecks === 'all' ? (t.wreckSlots ?? []) : wrecks).filter(
    (p): p is ArenaPointDef => !!p,
  );
  const blocked = new Uint8Array(cols * rows);
  const r2 = WRECK_RADIUS_M * WRECK_RADIUS_M;
  for (let cy = 0; cy < rows; cy++)
    for (let cx = 0; cx < cols; cx++) {
      const x = (cx + 0.5) * cellM;
      const y = (cy + 0.5) * cellM;
      let solid = blocks.some((b) => inside(b, x, y));
      if (!solid)
        solid = discs.some((w) => {
          const dx = x - num(w.x);
          const dy = y - num(w.y);
          return dx * dx + dy * dy <= r2;
        });
      blocked[cy * cols + cx] = solid ? 1 : 0;
    }
  return { cols, rows, cellM, sizeX, sizeY, blocked };
}

const isBlocked = (g: ArenaGrid, cx: number, cy: number) =>
  cx < 0 || cy < 0 || cx >= g.cols || cy >= g.rows || g.blocked[cy * g.cols + cx] === 1;
const centerOf = (g: ArenaGrid, i: number) => ({
  x: ((i % g.cols) + 0.5) * g.cellM,
  y: (Math.floor(i / g.cols) + 0.5) * g.cellM,
});

/** `ArenaRules.NearestFree`: the point's own (clamped) cell when free, else the free cell with the nearest centre; -1 on a solid grid. */
export function nearestFree(g: ArenaGrid, x: number, y: number): number {
  const cx = Math.max(0, Math.min(g.cols - 1, Math.floor(x / g.cellM)));
  const cy = Math.max(0, Math.min(g.rows - 1, Math.floor(y / g.cellM)));
  if (!isBlocked(g, cx, cy)) return cy * g.cols + cx;
  let bestIdx = -1;
  let best = Number.MAX_VALUE;
  for (let i = 0; i < g.blocked.length; i++) {
    if (g.blocked[i]) continue;
    const c = centerOf(g, i);
    const d = (c.x - x) * (c.x - x) + (c.y - y) * (c.y - y);
    if (d < best) {
      best = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** `ArenaRules.Overlaps`: the cell's square overlaps the rectangle. */
function overlaps(g: ArenaGrid, i: number, r: ArenaRectDef) {
  const x0 = (i % g.cols) * g.cellM;
  const y0 = Math.floor(i / g.cols) * g.cellM;
  return (
    x0 < num(r.x) + num(r.w) &&
    x0 + g.cellM > num(r.x) &&
    y0 < num(r.y) + num(r.h) &&
    y0 + g.cellM > num(r.y)
  );
}

/** The C# Dijkstra: 8-connected, diagonals cost cell x √2, no corner rule. */
function dijkstra(g: ArenaGrid, dist: Float64Array, prev: Int32Array, sources: number[]) {
  const heap: [number, number][] = [];
  const push = (d: number, i: number) => {
    heap.push([d, i]);
    let c = heap.length - 1;
    while (c > 0) {
      const p = (c - 1) >> 1;
      if (heap[p]![0] <= heap[c]![0]) break;
      [heap[p], heap[c]] = [heap[c]!, heap[p]!];
      c = p;
    }
  };
  const pop = () => {
    const top = heap[0]!;
    const last = heap.pop()!;
    if (heap.length) {
      heap[0] = last;
      let at = 0;
      for (;;) {
        const l = at * 2 + 1;
        const r = l + 1;
        let m = at;
        if (l < heap.length && heap[l]![0] < heap[m]![0]) m = l;
        if (r < heap.length && heap[r]![0] < heap[m]![0]) m = r;
        if (m === at) break;
        [heap[m], heap[at]] = [heap[at]!, heap[m]!];
        at = m;
      }
    }
    return top;
  };
  for (const s of sources) push(dist[s]!, s);
  const straight = g.cellM;
  const diagonal = g.cellM * Math.SQRT2;
  while (heap.length) {
    const [d, at] = pop();
    if (d > dist[at]!) continue;
    const cx = at % g.cols;
    const cy = Math.floor(at / g.cols);
    for (let oy = -1; oy <= 1; oy++)
      for (let ox = -1; ox <= 1; ox++) {
        if (ox === 0 && oy === 0) continue;
        const nx = cx + ox;
        const ny = cy + oy;
        if (isBlocked(g, nx, ny)) continue;
        const ni = ny * g.cols + nx;
        const nd = d + (ox !== 0 && oy !== 0 ? diagonal : straight);
        if (nd < dist[ni]!) {
          dist[ni] = nd;
          prev[ni] = at;
          push(nd, ni);
        }
      }
  }
}

export type RunLine = {
  /** Cell centres, start to exit; empty when unreachable. */
  points: { x: number; y: number }[];
  /** Metres to the nearest cell inside the grab radius (Infinity when unreachable). */
  toObjectiveM: number;
  /** Metres through the prize to the exit (Infinity when unreachable). */
  totalM: number;
  objectiveReachable: boolean;
  exitReachable: boolean;
};

/** `ArenaRules.ShortestRun` on the grid: start → within the grab radius of the prize → a cell overlapping the exit. */
export function shortestRunOn(g: ArenaGrid, t: ArenaTemplateDef, grabRadiusM: number): RunLine {
  const line: RunLine = {
    points: [],
    toObjectiveM: Infinity,
    totalM: Infinity,
    objectiveReachable: false,
    exitReachable: false,
  };
  const n = g.cols * g.rows;
  const start = nearestFree(g, num(t.start?.x), num(t.start?.y));
  if (n <= 0 || start < 0) return line;
  const d1 = new Float64Array(n).fill(Infinity);
  const p1 = new Int32Array(n).fill(-1);
  d1[start] = 0;
  dijkstra(g, d1, p1, [start]);
  const ox = num(t.objective?.at?.x);
  const oy = num(t.objective?.at?.y);
  const rr = Math.max(0.5, grabRadiusM);
  const ring: number[] = [];
  for (let i = 0; i < n; i++) {
    if (g.blocked[i]) continue;
    const c = centerOf(g, i);
    const dx = c.x - ox;
    const dy = c.y - oy;
    if (dx * dx + dy * dy <= rr * rr && Number.isFinite(d1[i]!)) ring.push(i);
  }
  if (!ring.length) {
    const cell = nearestFree(g, ox, oy);
    if (cell >= 0 && Number.isFinite(d1[cell]!)) ring.push(cell);
  }
  let best = Infinity;
  for (const c of ring) best = Math.min(best, d1[c]!);
  line.toObjectiveM = best;
  line.objectiveReachable = Number.isFinite(best);
  if (!line.objectiveReachable) return line;
  const d2 = new Float64Array(n).fill(Infinity);
  const p2 = new Int32Array(n).fill(-1);
  for (const c of ring) d2[c] = d1[c]!;
  dijkstra(g, d2, p2, ring);
  const exit = t.exit ?? { x: 0, y: 0, w: 0, h: 0 };
  let exitCell = -1;
  let total = Infinity;
  for (let i = 0; i < n; i++) {
    if (g.blocked[i] || !Number.isFinite(d2[i]!) || !overlaps(g, i, exit)) continue;
    if (d2[i]! < total) {
      total = d2[i]!;
      exitCell = i;
    }
  }
  line.totalM = total;
  line.exitReachable = Number.isFinite(total);
  if (exitCell < 0) return line;
  const cells: number[] = [];
  let at = exitCell;
  while (at >= 0 && p2[at]! >= 0) {
    cells.push(at);
    at = p2[at]!;
  }
  while (at >= 0) {
    cells.push(at);
    at = p1[at]!;
  }
  line.points = cells.reverse().map((c) => centerOf(g, c));
  return line;
}

export type RunPreview = RunLine & {
  /** Plain-language problems for the preview (the validator's own errors show inline next to the fields). */
  problems: string[];
};

/** The preview's run: the grid with every wreck slot, the grab radius from the rules (8 m), and what went wrong. */
export function shortestRun(t: ArenaTemplateDef, grabRadiusM = 8): RunPreview {
  const g = buildGrid(t);
  if (!g)
    return {
      points: [],
      toObjectiveM: Infinity,
      totalM: Infinity,
      objectiveReachable: false,
      exitReachable: false,
      problems: ['The grid is too big to preview (over 400 cells a side).'],
    };
  const line = shortestRunOn(g, t, grabRadiusM);
  const problems: string[] = [];
  if (!line.objectiveReachable) problems.push("The objective can't be reached from the start.");
  else if (!line.exitReachable) problems.push("The exit can't be reached from the objective.");
  return { ...line, problems };
}

/** Fuel can slots inside a block (the validator's "a fuel can inside a block"), by slot index. */
export function cansInBlocks(t: ArenaTemplateDef): number[] {
  const blocks = (t.blocks ?? []).filter((b): b is ArenaRectDef => !!b);
  return (t.fuelCanSlots ?? []).flatMap((p, i) =>
    p && blocks.some((b) => inside(b, num(p.x), num(p.y))) ? [i] : [],
  );
}

/**
 * `ArenaRules.MinRunSeconds` for the preview: the run's metres x `minRunSlack` at top speed, plus the grab for the cargo (or the pick-up).
 * Null when the vehicle can't move or the run is unreachable.
 */
export function minRunSeconds(
  run: RunLine,
  topSpeedMps: number,
  rules: {
    minRunSlack?: number;
    pickupS?: number;
    grab?: { baseS?: number; perKgS?: number } | null;
  },
  objectiveKind: string,
  cargoKg: number,
): number | null {
  if (!(topSpeedMps > 0) || !Number.isFinite(run.totalM)) return null;
  const slackRaw = num(rules.minRunSlack, 0.85);
  const slack = slackRaw > 0 && slackRaw <= 1 ? slackRaw : 0.85;
  const objectiveS =
    objectiveKind === 'survivor'
      ? Math.max(0, num(rules.pickupS, 4))
      : Math.max(0, num(rules.grab?.baseS, 2.5)) +
        Math.max(0, num(rules.grab?.perKgS, 0.03)) * Math.max(0, cargoKg);
  return (run.totalM * slack) / topSpeedMps + objectiveS;
}
